import { useQuery } from '@tanstack/react-query';
import whatsappService from '@/services/api/whatsapp.service';
import { whatsAppPhoneDigits } from '@/hooks/useWhatsAppUnread';

// "The agent is working on this thread right now" — the spinner that replaces
// the unread counter on inbox rows, WhatsApp icon badges and the drawer's
// sender picker. Display only: nothing here changes read state or filtering.
//
// Shape mirrors the unread map so a badge looks both up the same way:
// `{conversation_ids: [...], phones: {digits: [ownerUserId]}, contacts: {HASH: [ownerUserId]}}`.
//
// Polled, not pushed: runs start in the triage brain and end in the agent
// daemon, and neither can reach the browser's socket. Fast while something is
// running (a run lasts ~30 s), slow when idle; the backend caches a few
// seconds, so every open tab together costs one query. The app-level
// whatsapp_update listener also refreshes it on messages and plan events.
export const WHATSAPP_AGENT_RUNNING_QUERY_KEY = ['whatsapp', 'agentRunning'];

const EMPTY = { conversation_ids: [], phones: {}, contacts: {} };
const NO_OWNERS = {};

export const useWhatsAppAgentRunningMap = () => {
  const { data } = useQuery({
    queryKey: WHATSAPP_AGENT_RUNNING_QUERY_KEY,
    queryFn: () => whatsappService.getAgentRunning(),
    // Fetching belongs to the single useWhatsAppBadgeFeeds owner in AppRoutes.
    enabled: false,
  });
  return data || EMPTY;
};

// Owner accounts with a live run for this phone, falling back to the contact
// hash for threads without a phone — the same lookup order as the unread map.
const ownersFor = (map, phone, contactHashId) => {
  const digits = whatsAppPhoneDigits(phone);
  const byPhone = digits ? map.phones?.[digits] : null;
  if (byPhone?.length) return byPhone;
  if (contactHashId) {
    const byContact = map.contacts?.[String(contactHashId).toUpperCase()];
    if (byContact?.length) return byContact;
  }
  return null;
};

/** True while the agent works on any of this number's threads. */
export const useWhatsAppAgentRunning = (phone, contactHashId) => {
  const map = useWhatsAppAgentRunningMap();
  return Boolean(ownersFor(map, phone, contactHashId));
};

/** `{ownerUserId: true}` — which of our accounts the agent is working on for this number. */
export const useWhatsAppAgentRunningByOwner = (phone, contactHashId) => {
  const map = useWhatsAppAgentRunningMap();
  const owners = ownersFor(map, phone, contactHashId);
  if (!owners) return NO_OWNERS;
  return Object.fromEntries(owners.map((id) => [String(id), true]));
};

/** Set of conversation ids (as strings) the agent is working on. */
export const useWhatsAppAgentRunningConversations = () => {
  const map = useWhatsAppAgentRunningMap();
  return new Set((map.conversation_ids || []).map(String));
};

export default useWhatsAppAgentRunningMap;
